import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Plus, X, UserRound, Building2 } from 'lucide-react';
import { searchNeedle, matchesSearch } from '../lib/listSearch.mjs';

const ROLES = ['architect', 'gc', 'owner', 'concrete', 'other'];

const WATCHLIST_STAGE_COLOR = {
  watching:      'bg-slate-100 text-slate-600',
  outreach_sent: 'bg-blue-100 text-blue-600',
  engaged:       'bg-indigo-100 text-indigo-600',
  specifying:    'bg-amber-100 text-amber-700',
  won:           'bg-green-100 text-green-700',
  lost:          'bg-gray-100 text-gray-500',
};

const ROLE_LABEL = {
  owner_developer: 'Owner/Dev',
  general_contractor: 'GC',
  subcontractor: 'Sub',
  architect: 'Architect',
  design_firm: 'Design Firm',
  engineer: 'Engineer',
  other: 'Other',
};

// The shared helper sends an expired session to sign-in and puts the HTTP status on the error, as the page's reads do.
function apiPatch(path, body) {
  return api.patch(path, body);
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [viewId, setViewId] = useState(null);    // contact drawer (detail view)
  const [editing, setEditing] = useState(null);  // edit modal: contact object or 'new'
  const [q, setQ] = useState('');
  const seq = useRef(0); // Only the latest list request may update page state.

  // Only a good load knows the count; a failed first load shows its error, not "0 people" or "No contacts yet".
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const [c, a] = await Promise.all([api.get('/crm/contacts'), api.get('/crm/accounts')]);
      if (mine !== seq.current) return;
      setContacts(c.contacts || []); setAccounts(a.accounts || []); setError(''); setLoaded(true);
    } catch (e) { if (mine === seq.current) setError(e.message); }
    finally { if (mine === seq.current) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const needle = searchNeedle(q);
  const filtered = contacts.filter((c) => matchesSearch(needle, [c.name, c.email, c.phone, c.account_name]));

  if (loading) return <div className="p-8 text-[var(--text-secondary)]">Loading contacts…</div>;

  return (
    <div className="sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Contacts</h1>
          {loaded && <p className="text-sm text-[var(--text-secondary)]">{contacts.length} {contacts.length === 1 ? 'person' : 'people'}</p>}
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800 sm:flex-none" />
          <button onClick={() => setEditing('new')}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            <Plus size={16} /> Add Contact
          </button>
        </div>
      </div>
      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[var(--text-secondary)] dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2.5">Name</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Company</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Email</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Phone</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Role</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => setViewId(c.id)}
                className="cursor-pointer border-t border-[var(--border)] bg-white hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="break-words px-4 py-3 font-medium text-[var(--text-primary)]">{c.name}{c.title && <span className="ml-2 text-xs font-normal text-[var(--text-secondary)]">{c.title}</span>}
                  <span className="mt-0.5 block break-all text-xs font-normal text-[var(--text-secondary)] sm:hidden">{[c.account_name, c.email, c.phone].filter(Boolean).join(' · ')}</span></td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{c.account_name || '—'}</td>
                <td className="hidden sm:table-cell px-4 py-3">{c.email ? <a className="text-primary hover:underline" href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>{c.email}</a> : '—'}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{c.phone || '—'}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{c.role || '—'}</td>
              </tr>
            ))}
            {!filtered.length && !!contacts.length && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--text-secondary)]">No contacts match "{q.trim()}".</td></tr>
            )}
            {!contacts.length && (loaded ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--text-secondary)]">
                No contacts yet — convert a lead from the Leads board, or add one manually.</td></tr>
            ) : null)}
          </tbody>
        </table>
      </div>

      {viewId && (
        <ContactDrawer
          contactId={viewId}
          onClose={() => setViewId(null)}
          onEdit={(c) => { setViewId(null); setEditing(c); }}
          onChanged={load}
        />
      )}

      {editing && (
        <ContactModal
          contact={editing === 'new' ? null : editing}
          accounts={accounts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ContactDrawer({ contactId, onClose, onEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setData(await api.get(`/crm/contacts/${contactId}`)); setErr(''); }
    catch (e) { setErr(e.message); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    if (!err) return null;
    // Keep the drawer visible so a failed read explains why it could not open.
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
        <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
            <p className="text-sm text-rose-600">{err}</p>
            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>
      </div>
    );
  }
  const { contact, account, projects } = data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
              <UserRound size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-primary)]">{contact.name}</h2>
              {contact.title && <p className="text-sm text-[var(--text-secondary)]">{contact.title}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(contact)}
              className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-gray-50">
              Edit
            </button>
            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>

        {/* Contact info */}
        <div className="border-b border-[var(--border)] p-5 text-sm space-y-1.5">
          {contact.email && (
            <p><a className="text-primary hover:underline" href={`mailto:${contact.email}`}>{contact.email}</a></p>
          )}
          {contact.phone && <p className="text-[var(--text-secondary)]">{contact.phone}</p>}
          {contact.role && <p className="text-[var(--text-secondary)]">Role: {contact.role}</p>}
          {contact.notes && (
            <p className="text-xs text-[var(--text-secondary)] italic leading-relaxed">{contact.notes.slice(0, 300)}{contact.notes.length > 300 ? '…' : ''}</p>
          )}
        </div>

        {/* Account */}
        <div className="border-b border-[var(--border)] p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Company</h3>
          {account ? (
            <div className="rounded-lg border border-[var(--border)] p-3 text-sm">
              <p className="flex items-center gap-2 font-medium text-[var(--text-primary)]"><Building2 size={13} /> {account.name}</p>
              {account.vertical && <p className="text-xs text-[var(--text-secondary)]">{account.vertical}</p>}
              {account.domain && <p className="text-xs text-[var(--text-secondary)]">{account.domain}</p>}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">No company linked.</p>
          )}
        </div>

        {/* Projects where this contact is the key contact */}
        <div className="flex-1 p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
            Projects (key contact on {projects?.length || 0})
          </h3>
          {(projects || []).length === 0 && (
            <p className="text-sm text-[var(--text-secondary)]">Not listed as key contact on any project yet.</p>
          )}
          {(projects || []).map((p) => (
            <div key={p.junction_id} className="mb-2 rounded-lg border border-[var(--border)] p-3 text-sm">
              <div className="flex items-start gap-2">
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${WATCHLIST_STAGE_COLOR[p.watchlist_stage] || 'bg-gray-100 text-gray-500'}`}>
                  {p.watchlist_stage?.replace(/_/g, ' ') || '?'}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-[var(--text-primary)] truncate">{p.project_name || '—'}</p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {ROLE_LABEL[p.role] || p.role}
                    {(p.city || p.region) && ` · ${[p.city?.split(',')[0], p.region].filter(Boolean).join(', ')}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactModal({ contact, accounts, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: contact?.name || '', email: contact?.email || '', phone: contact?.phone || '',
    title: contact?.title || '', role: contact?.role || '', account_id: contact?.account_id || '',
    notes: contact?.notes || '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name required'); return; }
    setSaving(true);
    try {
      const body = { ...form, account_id: form.account_id || null };
      for (const k of Object.keys(body)) if (body[k] === '') body[k] = null;
      if (contact) await apiPatch(`/crm/contacts/${contact.id}`, body);
      else await api.post('/crm/contacts', body);
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md space-y-3 rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--text-primary)]">
            <UserRound size={18} /> {contact ? 'Edit Contact' : 'Add Contact'}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={16} /></button>
        </div>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Name *<input className={input} value={form.name} onChange={set('name')} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Email<input type="email" className={input} value={form.email} onChange={set('email')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Phone<input className={input} value={form.phone} onChange={set('phone')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Title<input className={input} value={form.title} onChange={set('title')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Role
            <select className={input} value={form.role || ''} onChange={set('role')}>
              <option value="">—</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Account
          <select className={input} value={form.account_id || ''} onChange={set('account_id')}>
            <option value="">— none —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Notes<textarea rows={3} className={input} value={form.notes} onChange={set('notes')} /></label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
